/**
 * specification-container.js
 * ------------------------------------------------------------
 * • Splittet die Spezifikations-Tabelle ab 768 px Breite in zwei
 *   balancierte Spalten, ohne IDs zu duplizieren.
 * • Nutzt textContent statt innerHTML → weniger XSS-Risiko,
 *   weniger Fehl-Treffer.
 * • Verschiebt Zeilen per appendChild → korrekte Semantik.
 * • Idempotent & reagiert auf Viewport-Änderungen.
 * • Keine Inline-Styles: .spec-table-wrapper bekommt eigene CSS-Klasse.
 * ------------------------------------------------------------
 */

(() => {
  const mq = window.matchMedia('(min-width: 768px)');
  let originalMarkup = null;                                  // für revert()

  /* -------------------------------------------------------- *
   *  Hilfsfunktionen
   * -------------------------------------------------------- */

  // einmalig grundlegendes Flex-Layout für den Wrapper injizieren
  function ensureWrapperCSS() {
    if (document.getElementById('spec-table-wrapper-style')) return;

    const style = document.createElement('style');
    style.id = 'spec-table-wrapper-style';
    style.textContent = `
      .spec-table-wrapper{
        display:flex;
        align-items:flex-start;
        justify-content:space-between;
        gap:2rem;
      }
    `;
    document.head.appendChild(style);
  }

  // Separatoren zählen nicht zur „effektiven“ Gruppengröße
  const effSize = (grp) => grp.filter(tr => !tr.querySelector('.separator-inner')).length;

  /** Algorithmus: Gruppen so verteilen, dass links und rechts
   *  möglichst gleich viele Zeilen landen (incl. Prior-Zeilen).
   */
  function splitByRowCount(groups, priors = []) {
    if (!groups.length) return { left: [], right: [] };
    const fixedLeft = priors.length;

    const prefix = [];
    let sum = 0;
    groups.forEach((g, i) => {
      sum += effSize(g);
      prefix[i] = sum;
    });

    const total   = sum + fixedLeft;
    const target  = total / 2;
    let bestIdx   = -1;
    let bestDiff  = Math.abs(fixedLeft - target);

    for (let i = 0; i < prefix.length; i++) {
      const diff = Math.abs(prefix[i] + fixedLeft - target);
      if (diff <= bestDiff) {
        bestDiff = diff;
        bestIdx  = i;
        if (diff === 0) break;
      }
    }

    return {
      left : bestIdx === -1 ? [] : groups.slice(0, bestIdx + 1),
      right: bestIdx === -1 ? groups : groups.slice(bestIdx + 1)
    };
  }

  // Bei exakt einer Kategorie: optional simpler 50/50-Split
  function simpleSplit(groups, priors, tableHeight) {
    if (tableHeight <= 500) return { left: groups, right: [] };
    const all = groups[0];
    const mid = Math.ceil(all.length / 2);
    return { left: [all.slice(0, mid)], right: [all.slice(mid)] };
  }

  // Letzten Separator in linker Spalte ausblenden
  function hideTrailingSeparator() {
    const left = document.querySelector('.specification-table-2');
    if (!left) return;
    const seps = left.querySelectorAll('td:first-child .separator-inner');
    if (seps.length > 0) seps[seps.length - 1].style.display = 'none';
  }

  /* -------------------------------------------------------- *
   *  Hauptlogik: splitTable & revertTable
   * -------------------------------------------------------- */

  function splitTable() {
    // Guard: schon gesplittet?
    if (document.querySelector('.spec-table-wrapper')) return;

    const table = document.querySelector('.specification-table');
    if (!table) return;

    originalMarkup = table.outerHTML;               // für Revert

    // Alle Zeilen sammeln und Leerzeilen (2. Zelle leer) vorab entfernen
    const rows = [...table.querySelectorAll('tr')].filter(tr => {
      // Header-Zeilen (th) immer behalten
      if (tr.querySelector('th')) return true;

      const tds = tr.querySelectorAll('td');

      // Separator-Zeilen (nur 1 TD mit .separator-inner) behalten
      if (tds.length === 1) return true;

      // Nur Zeilen behalten, bei denen die Wert‑Zelle nicht leer ist
      return tds.length < 2 || tds[1].textContent.trim() !== '';
    });

    // Keine priorisierten Zeilen mehr – alle Zeilen bleiben in ihrer Gruppe
    const priors = [];
    const rest   = rows;

    // Gruppen anhand von Überschriften bilden
    const groups = [];
    let group = [];
    const isHeader = tr => tr.classList.contains('table-head-spacing');

    rest.forEach(tr => {
      if (isHeader(tr)) {
        if (group.length) groups.push(group);
        group = [tr];
      } else {
        group.push(tr);
      }
    });
    if (group.length) groups.push(group);

    // Leere Gruppen (nur Separatoren) am Anfang mit der Folge­gruppe
    // verschmelzen, damit sie nicht als eigene Spalte enden.

    if (groups.length > 1 && effSize(groups[0]) === 0) {
      groups[1] = [...groups[0], ...groups[1]];  // Separator(en) behalten
      groups.shift();                            // erste Gruppe entfernen
    }

    // Aufteilen
    const { left, right } = groups.length === 1
      ? simpleSplit(groups, priors, table.offsetHeight)
      : splitByRowCount(groups, priors);

    // Neues DOM aufbauen
    ensureWrapperCSS();
    const wrapper = document.createElement('div');
    wrapper.className = 'spec-table-wrapper';

    const buildCol = (rowsArr) => {
      const tbl  = document.createElement('table');
      tbl.className = 'specification-table-2';
      const frag = document.createDocumentFragment();
      rowsArr.forEach(tr => frag.appendChild(tr));      // verschiebt Zeilen
      tbl.appendChild(frag);
      return tbl;
    };

    wrapper.appendChild(buildCol([...priors, ...left.flat()]));
    wrapper.appendChild(buildCol(right.flat()));

    table.replaceWith(wrapper);

    hideTrailingSeparator();
    // checkTableHeight();    // disabled: expand/collapse feature removed
  }

  function revertTable() {
    const wrapper = document.querySelector('.spec-table-wrapper');
    if (!wrapper || !originalMarkup) return;

    const temp = document.createElement('div');
    temp.innerHTML = originalMarkup.trim();             // neue DOM-Nodes
    wrapper.replaceWith(temp.firstChild);
    originalMarkup = null;
  }

  /* -------------------------------------------------------- *
   *  Öffentliche Helfer (minimal angepasst)
   * -------------------------------------------------------- */

  // window.toggleSpecifications = function toggleSpecifications() {
  //   const container = document.querySelector('.product-spec');
  //   const button    = document.querySelector('.expand-btn');
  //   if (!container || !button) return;
  //   const expanded = container.classList.toggle('expanded');
  //   button.textContent = button.getAttribute(expanded ? 'data-hide' : 'data-show');
  //   const isMobile = window.matchMedia('(max-width: 767px)').matches;
  //   const h        = container.scrollHeight;
  //   if (!expanded && (isMobile || h > 2000)) {
  //     button.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //   }
  // };
  window.toggleSpecifications = function toggleSpecifications() { /* disabled */ };

  // window.checkTableHeight = function checkTableHeight() {
  //   const tableSpec  = document.querySelector('.specification-table, .specification-table-2');
  //   const productSpec = document.querySelector('.product-spec');
  //   const button     = document.querySelector('.expand-btn');
  //   if (!tableSpec || !productSpec) return;
  //   if (tableSpec.offsetHeight < 550) {
  //     if (button) button.style.display = 'none';
  //     productSpec.style.maxHeight = 'none';
  //     if (!document.getElementById('spec-table-hide-after')) {
  //       const style = document.createElement('style');
  //       style.id = 'spec-table-hide-after';
  //       style.textContent = '.product-spec::after{content:none !important;display:none !important;}';
  //       document.head.appendChild(style);
  //     }
  //   }
  // };
  window.checkTableHeight = function checkTableHeight() { /* disabled */ };

  /* -------------------------------------------------------- *
   *  Initialisierung
   * -------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded',   () => mq.matches ? splitTable() : revertTable());
  mq.addEventListener ? mq.addEventListener('change', e => e.matches ? splitTable() : revertTable())
                      : mq.addListener(         e => e.matches ? splitTable() : revertTable());
})();
